import classNames from 'classnames/bind';
import { IPromotion } from 'common/promotion';
import { useCurationContext } from 'contexts/curation';
import { useReloadTimerContext } from 'contexts/reloadTimer';
import { TripType } from 'contexts/searchParams';
import { useEffect, useRef, useState } from 'react';
import ItemPriceList from '../item/priceList';
import getPromotions from '../promotion';
import {
  IFareTypeList,
  IInternationalContextResult,
  IScheduleDetail,
  ScheduleKeys,
} from '../types';
import { hasAdultNaverFare } from '../utils';
import styles from './index.module.scss';
import RoundDiffAL from './itemDiff';
import RoundSameAL from './itemSame';

const cx = classNames.bind(styles);

interface IProps {
  fare: IFareTypeList;
  fareKeys: string[];
  flightResult: IInternationalContextResult;
  isTopPopularFlight?: boolean;
  journeyTimes: string[][];
  nClickKey: string;
  otherItems: string[];
  promotions: IPromotion[];
  sch: string;
  schedule: IScheduleDetail[][];
  showHelpTooltip?: boolean;
  // 요금 유형 unfold시 상단 강제 이동이 필요한 경우를 위해 추가함
  moveToTop?: (offset: number) => void;
  selectFlights: (
    sch: string,
    fareType: string,
    airline: string,
    labels: string[],
  ) => void;
}

const getArrayProp = (detail: IScheduleDetail[], key: ScheduleKeys) =>
  detail.map(d => d[key]);

const ConcurrentItemContainer = ({
  fare,
  fareKeys,
  flightResult,
  isTopPopularFlight = false,
  journeyTimes,
  nClickKey = 'isr',
  otherItems,
  promotions,
  sch,
  schedule,
  showHelpTooltip,
  moveToTop,
  selectFlights,
}: IProps) => {
  const { airlines = {}, fareTypes = {}, airports = {} } = flightResult;
  const { withReloadTimer } = useReloadTimerContext();
  const [depSchedule, arrSchedule] = schedule;
  const [depAirlines, arrAirlines] = [
    getArrayProp(depSchedule, ScheduleKeys.airlineVendor),
    getArrayProp(arrSchedule, ScheduleKeys.airlineVendor),
  ];
  const ItemSchedule =
    new Set([...depAirlines, ...arrAirlines]).size ===
    Math.max(new Set(depAirlines).size, new Set(arrAirlines).size)
      ? RoundSameAL
      : RoundDiffAL;
  const [foldFares, toggleFoldFares] = useState(true);
  const resultDivRef = useRef<HTMLDivElement | null>(null);
  const { getLabels, isCurationTarget } = useCurationContext();
  const minFare = fare[fareKeys[0]][0];
  const [promotion] = getPromotions(promotions, schedule, minFare);
  const labels = Array.from(getLabels(sch));

  useEffect(() => {
    if (window?.innerWidth >= 1090 && !foldFares && moveToTop) {
      moveToTop(resultDivRef.current?.offsetTop || 0);
    }
  }, [foldFares]);

  return (
    <div
      className={cx('ConcurrentItemContainer', {
        with_labels: isCurationTarget(sch) || otherItems.length > 0,
      })}
      ref={resultDivRef}
    >
      <div className={cx('inner')}>
        <div className={cx('schedule')}>
          <ItemSchedule
            className={cx('item')}
            airlines={airlines}
            carbonEmissionAverages={flightResult.carbonEmissionAverage}
            fareType={fareTypes[fareKeys[0]]}
            isDirectPromotion={hasAdultNaverFare(minFare)}
            journeyTimes={journeyTimes}
            otherItems={otherItems}
            promotion={promotion}
            sch={sch}
            schedule={schedule}
            airports={airports}
            showHelpTooltip={showHelpTooltip}
          />
        </div>
        <button
          className={cx('select_schedule')}
          type="button"
          data-event-nlog="click"
          data-event-area-code={`${nClickKey}.${isTopPopularFlight ? 'top' : ''}schedule1`}
          onClick={() => {
            if (fareKeys.length === 1) {
              withReloadTimer(selectFlights)(
                sch,
                fareKeys[0],
                depAirlines[0] as string,
                labels,
              );
            } else {
              toggleFoldFares(!foldFares);
            }
          }}
        />
        <hr />
        <ItemPriceList
          fare={fare}
          fareKeys={fareKeys}
          fareTypes={fareTypes}
          foldFares={foldFares}
          isTopPopularFlight={isTopPopularFlight}
          nClickKey={nClickKey}
          promotions={promotions}
          scheduleIndex={0}
          schedules={schedule}
          tripType={TripType.RT}
          selectFlight={withReloadTimer((fareType: string) =>
            selectFlights(sch, fareType, depAirlines[0] as string, labels),
          )}
          toggleFoldFares={toggleFoldFares}
        />
      </div>
    </div>
  );
};

export default ConcurrentItemContainer;
